/* eslint-disable @typescript-eslint/no-explicit-any */
import { GetStaticProps } from 'next'
import dynamic from 'next/dynamic'
import { withRouter } from 'next/router'
import React, { ComponentType } from 'react'
import HeaderDescription from '../components/HeaderDescription'
import { LazyComponent } from '../components/LazyComponent'
import { DefaultLayout } from '../components/layouts/DefaultLayout'
import { SettingsContext } from '../contexts/settingsContext'
import { useRedirects } from '../hooks/useRedirects'
import Footer from '../slices/Footer'
import storyblokInstance, { default as StoryblokService } from '../utils/storyblokService'

// https://vercel.com/docs/infrastructure/data-cache/manage-data-cache#disabling-vercel-data-cache
export const fetchCache = 'force-no-store'

type ComponentRegistry = {
  [key: string]: ComponentType<any>
}

const SeoCard = dynamic(() => import('../slices/SeoCard'))

const Components: ComponentRegistry = {
  basic_article: dynamic(() => import('../slices/BasicArticle')),
  two_col_collapsable: dynamic(() => import('../slices/TwoColCollapsable')),
  home_featured_posts: dynamic(() => import('../slices/FeaturedPosts')),
  flexible_content: dynamic(() => import('../slices/FlexibleContent')),
  team_card: dynamic(() => import('../slices/TeamCard')),
  fcp_gallery: dynamic(() => import('../components/SectionGallery')),
  fcp_downloads: dynamic(() => import('../slices/DownloadList')),
  home_banner_and_video: dynamic(() => import('../slices/Home/HomeBanerAndVideo/HomeBannerAndVideo')),
  home_featured_projects: dynamic(() => import('../components/FeaturedCarousel')),
  home_services_tab_component: dynamic(() => import('../slices/Home/HomeTabbedCarousel/HomeTbbedCarousel')),
  introduction: dynamic(() => import('../slices/Introduction')),
  logo_grid: dynamic(() => import('../slices/LogoGrid')),
  fcp_related_content: dynamic(() => import('../slices/RelatedContent')),
  inframe_carousel: dynamic(() => import('../slices/ServiceDetails/InframeCarousel')),
  embedable_video: dynamic(() => import('../slices/EmbeddableVideo')),
  detail_vertical_tabs: dynamic(() => import('../slices/ServiceDetails/DetailVerticalTab')),
  services_detail: dynamic(() => import('../slices/ServiceDetails/ImageColumns')),
  table: dynamic(() => import('../slices/Table')),
  quote_carousel: dynamic(() => import('../slices/QuoteCarousel')),
  big_image_component: dynamic(() => import('../slices/BigImage')),
  curvey_hero: dynamic(() => import('../slices/CurvyImageDivider')),
  project_highlights: dynamic(() => import('../slices/HighlightCarousel')),
  project_listing_banner: dynamic(() => import('../slices/ProjectListingBanner')),
}

type Props = {
  doc: any
  settings: any
  menus: Record<string, unknown>
  footers: Record<string, unknown>
  router: any
}

class DynamicPage extends React.Component<Props> {
  state: {
    locale: string
    path: string
    story: any
  }

  constructor(props: Props) {
    super(props)
    this.state = {
      locale: props.router.locale,
      path: props.router.asPath,
      story: props.doc?.data?.story,
    }
  }

  componentDidMount() {
    StoryblokService.initEditor(this as any)
  }

  static getDerivedStateFromProps(props: Props, state: any) {
    const propsStory = props?.doc?.data?.story

    if (props.router.locale !== state.locale || props.router.asPath !== state.path) {
      return {
        locale: props.router.locale,
        path: props.router.asPath,
        story: propsStory,
      }
    }

    return null
  }

  render() {
    // Top-level hook for the visual editor
    const content = this.state.story.content
    const siteTitle = this.props.settings?.site_name ?? 'undefined'

    return (
      <div>
        <DefaultLayout title={siteTitle}>
          <SettingsContext.Provider value={this.props.settings}>
            {content?.head && content?.head.length > 0 && (
              <SeoCard slice={content?.head[0]} />
            )}
            {(content?.component !== 'project-details' && content?.header && content?.header.length > 0) && <HeaderDescription headerData={content?.header[0]} />}
            <article>
              {content?.body?.map((slice: any) => {
                const Component = Components[slice?.component]

                return Component ? (
                  <LazyComponent performanceMarkName={slice.component}>
                    <Component
                      slice={slice}
                      key={slice._uid}
                      settings={this.props.settings}
                      footers={this.props.footers}
                      menu={this.props.menus} />
                  </LazyComponent>
                ) : null
              })}
            </article>
            <Footer cta={content?.footer_cta} static_footer={content?.static_footer} />
          </SettingsContext.Provider>
        </DefaultLayout>
      </div>
    )
  }
}

export const getStaticProps: GetStaticProps = async (context) => {
  const slug = (Array.isArray((context.params?.slug)) ? context.params?.slug.join('/') : (context.params?.slug )) ?? ''
  const isPreview: boolean | undefined = (context?.previewData as {isPreview: boolean})?.isPreview

  const settingsDoc = await StoryblokService.getDocumentBySlug('global-content-types/settings')
  const menuDocs = await StoryblokService.getCollection('menus')
  const footerDocs = await StoryblokService.getCollection('footers')
  const doc = await StoryblokService.getDocumentBySlug(slug, {
    resolve_relations: 'featured_posts.posts,fcp_related_content.posts,team_card.image_source,footer_related.pages,project_listing_banner.banner',
    resolve_links: 'story',
    version: isPreview ? 'draft' : 'published',
  }) ?? null

  if (!doc?.data?.story) {
    // Redirect if needed
    const redirect = useRedirects(slug)

    if (redirect) {
      return {
        redirect: {
          permanent: redirect.permanent,
          destination: redirect.destination,
        },
      }
    }

    return {
      notFound: true,
      revalidate: 3600,
    }
  }

  return {
    props: {
      doc,
      footers: footerDocs,
      menus: menuDocs,
      settings: {
        ...settingsDoc?.data?.story?.content,
      },
    },
    revalidate: 300,
  }
}

export async function getStaticPaths() {
  const docs = await storyblokInstance.getAll()
  const mappedStories = docs.filter((doc: { slug: string; full_slug: string }) => doc.slug !== 'home' && doc.slug !== 'error-page' && doc.slug !== 'settings' && doc.slug !== 'main-navigation' && !doc.full_slug.startsWith('projects/') && !doc.full_slug.startsWith('team/') && !doc.full_slug.startsWith('blog/'))

  return {
    paths: mappedStories.map(doc => ({
      params: {
        slug: doc.full_slug.split('/'),
      },
    }),
    ),
    fallback: 'blocking',
  }
}

export default withRouter(DynamicPage as any)

